import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import {
  CreditCard,
  Check,
  Crown,
  Zap,
  Building2,
  Loader2,
  ArrowRight,
  Clock,
  AlertTriangle,
  CheckCircle,
  Receipt
} from 'lucide-react';

export default function Billing() {
  const navigate = useNavigate();
  const { tenant, getAuthHeaders, isFeatureRestricted } = useAuth();
  
  // Check access restriction
  useEffect(() => {
    if (isFeatureRestricted('billing')) {
      toast.error('Access denied. You do not have permission to access this page.');
      navigate('/dashboard');
    }
  }, [isFeatureRestricted, navigate]);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [packages, setPackages] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [processingCheckout, setProcessingCheckout] = useState(false);

  useEffect(() => {
    fetchBillingData();
  }, []);

  const fetchBillingData = async () => {
    try {
      const [subRes, pkgRes, txnRes] = await Promise.all([
        api.get('/billing/subscription', { headers: getAuthHeaders() }),
        api.get('/billing/packages', { headers: getAuthHeaders() }),
        api.get('/billing/transactions', { headers: getAuthHeaders() })
      ]);
      setSubscription(subRes.data);
      setPackages(pkgRes.data.packages);
      setTransactions(txnRes.data.transactions);
    } catch (error) {
      console.error('Failed to fetch billing data:', error);
      toast.error('Failed to load billing information');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (pkg) => {
    setSelectedPackage(pkg);
    setUpgradeDialogOpen(true);
  };

  const processCheckout = async () => {
    if (!selectedPackage) return;
    
    setProcessingCheckout(true);
    try {
      const response = await api.post('/billing/checkout', {
        package_id: selectedPackage.id,
        origin_url: window.location.origin
      }, { headers: getAuthHeaders() });

      // Redirect to Stripe Checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Checkout failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to create checkout session');
      setProcessingCheckout(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!confirm('Are you sure you want to cancel your subscription? You will be downgraded to the Free plan at the end of your billing period.')) {
      return;
    }

    try {
      await api.post('/billing/cancel', {}, { headers: getAuthHeaders() });
      toast.success('Subscription canceled');
      fetchBillingData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      trialing: 'bg-blue-500/20 text-blue-400',
      active: 'bg-green-500/20 text-green-400',
      past_due: 'bg-yellow-500/20 text-yellow-400',
      canceled: 'bg-red-500/20 text-red-400',
      pending: 'bg-orange-500/20 text-orange-400',
      paid: 'bg-green-500/20 text-green-400',
      failed: 'bg-red-500/20 text-red-400'
    };
    return styles[status] || 'bg-muted/500/20 text-muted-foreground';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const currentPlan = subscription?.plan;
  const usage = subscription?.usage || {};
  const limits = subscription?.limits || {};
  const trialEndsAt = subscription?.trial_ends_at ? new Date(subscription.trial_ends_at) : null;
  const daysLeft = trialEndsAt ? Math.max(0, Math.ceil((trialEndsAt - new Date()) / (1000 * 60 * 60 * 24))) : 0;

  // Group packages by plan
  const packagesByPlan = packages.reduce((acc, pkg) => {
    if (!acc[pkg.plan_slug]) acc[pkg.plan_slug] = [];
    acc[pkg.plan_slug].push(pkg);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {/* Trial Banner */}
      {subscription?.subscription_status === 'trialing' && (
        <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 border border-blue-500/20 rounded-xl p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-500/20 rounded-lg">
                <Clock className="h-6 w-6 text-blue-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Trial Period</h3>
                <p className="text-muted-foreground">
                  Your trial ends in <span className="text-blue-400 font-medium">{daysLeft} days</span>. 
                  Upgrade now to keep all your data and features.
                </p>
              </div>
            </div>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => handleUpgrade(packages.find(p => p.id === 'pro_monthly'))}>
              Upgrade Now
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Current Plan */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 bg-slate-800 border-slate-700">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-white">Current Plan</CardTitle>
                <CardDescription className="text-muted-foreground">Your subscription details</CardDescription>
              </div>
              <Badge className={getStatusBadge(subscription?.subscription_status)}>
                {subscription?.subscription_status}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-500/20 rounded-lg">
                <Crown className="h-6 w-6 text-purple-400" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">{currentPlan?.name || 'Free'} Plan</h3>
                <p className="text-muted-foreground">{currentPlan?.description}</p>
              </div>
            </div>

            {/* Usage Meters */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-slate-700/50 rounded-lg">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-muted-foreground">Users</span>
                  <span className="text-white">{usage.current_users || 0} / {limits.max_users || 0}</span>
                </div>
                <Progress 
                  value={((usage.current_users || 0) / (limits.max_users || 1)) * 100} 
                  className="h-2 bg-slate-600"
                />
              </div>
              <div className="p-4 bg-slate-700/50 rounded-lg">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-muted-foreground">Calls This Month</span>
                  <span className="text-white">{usage.calls_this_month || 0} / {limits.max_calls_per_month || 0}</span>
                </div>
                <Progress 
                  value={((usage.calls_this_month || 0) / (limits.max_calls_per_month || 1)) * 100} 
                  className="h-2 bg-slate-600"
                />
              </div>
              <div className="p-4 bg-slate-700/50 rounded-lg">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-muted-foreground">Storage</span>
                  <span className="text-white">{(usage.storage_used_gb || 0).toFixed(2)} / {limits.max_storage_gb || 0} GB</span>
                </div>
                <Progress 
                  value={((usage.storage_used_gb || 0) / (limits.max_storage_gb || 1)) * 100} 
                  className="h-2 bg-slate-600"
                />
              </div>
            </div>
          </CardContent>
          {subscription?.subscription_status === 'active' && (
            <CardFooter>
              <Button 
                variant="outline" 
                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                onClick={handleCancelSubscription}
              >
                Cancel Subscription
              </Button>
            </CardFooter>
          )}
        </Card>

        {/* Quick Stats */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white">Plan Features</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3">
              {currentPlan?.features?.map((feature, idx) => (
                <li key={idx} className="flex items-center gap-2 text-muted-foreground/70">
                  <CheckCircle className="h-4 w-4 text-green-400" />
                  {feature.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </li>
              )) || (
                <>
                  <li className="flex items-center gap-2 text-muted-foreground/70">
                    <CheckCircle className="h-4 w-4 text-green-400" />
                    Basic Analytics
                  </li>
                  <li className="flex items-center gap-2 text-muted-foreground/70">
                    <CheckCircle className="h-4 w-4 text-green-400" />
                    Call Scoring
                  </li>
                </>
              )}
            </ul>
          </CardContent>
        </Card>
      </div>

      {/* Available Plans */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white">Upgrade Your Plan</CardTitle>
          <CardDescription className="text-muted-foreground">Choose a plan that fits your needs</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {packages.map((pkg) => (
              <div 
                key={pkg.id}
                className={`p-6 rounded-xl border transition-all cursor-pointer ${
                  pkg.plan_slug === currentPlan?.slug
                    ? 'border-green-500/50 bg-green-500/10'
                    : 'border-slate-600 bg-slate-700/50 hover:border-blue-500/50'
                }`}
                onClick={() => pkg.plan_slug !== currentPlan?.slug && handleUpgrade(pkg)}
              >
                <div className="flex items-center gap-2 mb-3">
                  {pkg.plan_slug === 'pro' && <Zap className="h-5 w-5 text-blue-400" />}
                  {pkg.plan_slug === 'business' && <Building2 className="h-5 w-5 text-purple-400" />}
                  <h4 className="font-semibold text-white">{pkg.name}</h4>
                </div>
                <div className="mb-3">
                  <span className="text-2xl font-bold text-white">${pkg.amount}</span>
                  <span className="text-muted-foreground">/{pkg.interval === 'monthly' ? 'mo' : 'yr'}</span>
                </div>
                <p className="text-sm text-muted-foreground mb-4">{pkg.description}</p>
                {pkg.plan_slug === currentPlan?.slug ? (
                  <Badge className="bg-green-500/20 text-green-400 w-full justify-center">
                    <Check className="h-3 w-3 mr-1" /> Current Plan
                  </Badge>
                ) : (
                  <Button 
                    size="sm" 
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    onClick={(e) => { e.stopPropagation(); handleUpgrade(pkg); }}
                  >
                    Select Plan
                  </Button>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Payment History */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Receipt className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-white">Payment History</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {transactions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <CreditCard className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No payment history yet</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700">
                  <TableHead className="text-muted-foreground">Date</TableHead>
                  <TableHead className="text-muted-foreground">Package</TableHead>
                  <TableHead className="text-muted-foreground">Amount</TableHead>
                  <TableHead className="text-muted-foreground">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transactions.map((txn) => (
                  <TableRow key={txn.id} className="border-slate-700">
                    <TableCell className="text-muted-foreground/70">
                      {new Date(txn.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-white">
                      {packages.find(p => p.id === txn.package_id)?.name || txn.package_id}
                    </TableCell>
                    <TableCell className="text-white">
                      ${txn.amount} {txn.currency?.toUpperCase()}
                    </TableCell>
                    <TableCell>
                      <Badge className={getStatusBadge(txn.payment_status)}>
                        {txn.payment_status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Upgrade Dialog */}
      <Dialog open={upgradeDialogOpen} onOpenChange={setUpgradeDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Confirm Upgrade</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              You're about to upgrade to {selectedPackage?.name}
            </DialogDescription>
          </DialogHeader>
          {selectedPackage && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-slate-700/50 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-muted-foreground">Package</span>
                  <span className="text-white font-medium">{selectedPackage.name}</span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-muted-foreground">Price</span>
                  <span className="text-white font-medium">
                    ${selectedPackage.amount}/{selectedPackage.interval === 'monthly' ? 'month' : 'year'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Billing</span>
                  <span className="text-white font-medium capitalize">{selectedPackage.interval}</span>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                You'll be redirected to our secure payment page powered by Stripe. 
                Your subscription will be activated immediately after payment.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setUpgradeDialogOpen(false)}
              className="border-slate-600 text-muted-foreground/70"
            >
              Cancel
            </Button>
            <Button 
              className="bg-blue-600 hover:bg-blue-700"
              onClick={processCheckout}
              disabled={processingCheckout}
            >
              {processingCheckout ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Processing...</>
              ) : (
                <><CreditCard className="mr-2 h-4 w-4" /> Continue to Payment</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
